import React, { useCallback, useEffect, useState } from 'react'
import DocumentHeader from '../../../../documents/components/DocumentHeader'
import CommonInvoiceTemplate from '../../../../documents/components/CommonInvoiceTemplate'
import { SingleSalesReturnData, TableColumn } from '../../../../types'
import { useParams } from 'react-router-dom'
import { Box } from '@mui/material'
import { request } from '../../../../../../utils/request'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import PrintButtons from '../../../../../../components/PrintButtons'
import usePrint from '../../../../../../utils/usePrint'
import PrintableContainer from '../../../../../../components/PrintableContainer'
import { BusinessInfo, TaxInfo } from '../../../../../controlPanel/types'

const ShowSalesReturn = () => {
  const [loading, setLoading] = useState(true);
  const [singleSalesReturn, setSingleSalesReturn] = useState<SingleSalesReturnData | null>(null);
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>();
  const [taxInfo, setTaxInfo] = useState<TaxInfo>();
  const params = useParams()
  const salesReturnId = params.salesReturnId

  const { isPrinting, handlePrint } = usePrint();

  const fetchBusinessInfo = useCallback(async () => {
    try {
      const res = await request({
        url: "setting/businessInfo",
        method: "get",
      });

      if (res.success) {
        setBusinessInfo(res.data);
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Failed to fetch business info");
    }
  }, []);
  useEffect(() => {
    fetchBusinessInfo();
  }, [fetchBusinessInfo])

  const fetchTaxInfo = useCallback(async () => {
    try {
      const res = await request({
        url: "setting/taxInfo",
        method: "get",
      });

      if (res.success) {
        setTaxInfo(res.data);
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Failed to fetch tax info");
    }
  }, []);
  useEffect(() => {
    fetchTaxInfo();
  }, [fetchTaxInfo])

  const fetchSingleSalesReturn = useCallback(async () => {
    if (!salesReturnId) return;

    setLoading(true)
    try {
      const res = await request({
        url: `salesReturn/get-single-sales-return/${salesReturnId}`,
        method: "get",
      })
      if (res.success && res.data) {
        setSingleSalesReturn(res.data)
      } else {
        toast.error(res.message || "Failed to fetch Sales Return ");
      }
    } catch (error: any) {
      toast.error(error.message || "Error fetching Sales Return ");
    } finally {
      setLoading(false)
    }
  }, [salesReturnId])

  useEffect(() => {
    fetchSingleSalesReturn();
  }, [fetchSingleSalesReturn])

  // Format date to readable format
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return dayjs(dateString).format('DD MMM YYYY');
  };

  const onPrint = () => {
    // force re-render before invoking print
    requestAnimationFrame(() => {
      setTimeout(() => {
        handlePrint('sales_return_print', {
          title: `Sales Return - ${singleSalesReturn?.salesReturnNumber}'}`,
          fileName: `Sales _${singleSalesReturn?.salesReturnNumber}`,
          landscape: false,
          showHeader: true,
          showFooter: false,
          customStyles: `
          table { width: 100% !important; border-collapse: collapse !important; }
          th, td { border: 1px solid #ddd !important; padding: 6px !important; }
          th { background-color: #f5f5f5 !important; font-weight: bold !important; }
          .no-print { display: none !important; }
          .invoice-heading {text-align:center; font-size: 22px}
          .flex-center {display:flex; justify-content:space-between; align-items: flex-start; gap:5px}
          .sub-heading {font-size:14px; font-weight:bold}
          .text-bold {font-weight:bold}
          .w-50{width:50%}
          .out-border{border:1px solid black}
        `
        });
      }, 150); // small delay ensures browser repaints
    });
  };

  // Calculate totals from invoice items
  const calculateTotals = () => {
    if (!singleSalesReturn?.items?.length) {
      return {
        quantity: 0,
        taxableAmount: 0,
        tax: 0,
        amount: 0
      };
    }

    const totals = singleSalesReturn.items.reduce((acc, item) => {
      acc.quantity += item.quantity || 0;
      acc.amount += item.amount || 0;
      // Calculate tax amount based on item amount and tax rate
      const taxAmount = ((item.amount || 0) * (item.tax?.rate || 0)) / 100;
      acc.tax += taxAmount;
      // Taxable amount is amount minus tax (for GST calculations)
      acc.taxableAmount += (item.amount || 0) - taxAmount;
      return acc;
    }, { quantity: 0, taxableAmount: 0, tax: 0, amount: 0 });

    return totals;
  };

  const prepareTableData = () => {
    if (!singleSalesReturn?.items?.length) return [];

    return singleSalesReturn.items.map((item, index) => {
      const taxAmount = ((item.amount || 0) * (item.tax?.rate || 0)) / 100;
      return {
        id: item.id || index.toString(),
        item: item.itemName || `Item ${index + 1}`,
        hsnNo: item.hsnNo || 'N/A',
        quantity: item.quantity || 0,
        rate: item.price || 0,
        tax: taxAmount,
        taxPercentage: item.tax?.rate || 0,
        amount: item.amount || 0,
        discount: item.discount?.amount || 0,
        discountPercentage: item.discount?.isPercentage ? item.discount.amount : 0
      };
    });
  };

  // Prepare table columns based on GST type
  const prepareTableColumns = (): TableColumn[] => {
    const baseColumns: TableColumn[] = [
      // { id: 'srNo', label: 'Sr No', align: "left", width: 60 },
      { id: 'item', label: 'Item Description', align: "left", width: 200 },
      { id: 'quantity', label: 'Qty', align: "right", width: 80 },
      { id: 'rate', label: 'Rate (₹)', align: "right", width: 100 },
      { id: 'discount', label: 'Discount (₹)', align: "right", width: 100 },
    ];

    if (singleSalesReturn?.gstType === 'GST') {
      // baseColumns.push({ id: 'hsnNo', label: 'HSN/SAC', align: "left", width: 100 });
    }

    baseColumns.push(
      // { id: 'taxPercentage', label: 'Tax %', align: "right", width: 80 },
      { id: 'tax', label: 'Tax Amt (₹)', align: "right", width: 100 },
      { id: 'amount', label: 'Amount (₹)', align: "right", width: 120 }
    );

    return baseColumns;
  };

  // Prepare summary items
  const prepareSummaryItems = () => {
    if (!singleSalesReturn) return [];

    const totals = calculateTotals();
    const summaryItems = [];

    // Taxable Amount
    summaryItems.push({
      label: 'Taxable Amount',
      value: singleSalesReturn?.taxableAmount,
      variant: 'body2' as const
    });

    // GST Breakdown if GST Sales Return 
    if (singleSalesReturn.gstType === 'GST' && singleSalesReturn.taxBreakdown?.length > 0) {
      singleSalesReturn.taxBreakdown.forEach((taxItem, index) => {
        if (taxItem.cgst > 0) {
          summaryItems.push({
            label: `CGST (${taxItem.cgst}%)`,
            value: (totals.taxableAmount * taxItem.cgst) / 100,
            variant: 'body2' as const
          });
        }
        if (taxItem.sgst > 0) {
          summaryItems.push({
            label: `SGST (${taxItem.sgst}%)`,
            value: (totals.taxableAmount * taxItem.sgst) / 100,
            variant: 'body2' as const
          });
        }
        if (taxItem.igst > 0) {
          summaryItems.push({
            label: `IGST (${taxItem.igst}%)`,
            value: (totals.taxableAmount * taxItem.igst) / 100,
            variant: 'body2' as const
          });
        }
      });
    } else if (singleSalesReturn.gstType === 'GST') {
      // Fallback if no tax breakdown
      summaryItems.push({
        label: `GST (${singleSalesReturn.items[0]?.tax?.rate || 0}%)`,
        value: totals.tax,
        variant: 'body2' as const
      });
    }

    // Additional Charges
    if (singleSalesReturn.charges?.length > 0) {
      singleSalesReturn.charges.forEach(charge => {
        summaryItems.push({
          label: charge.label,
          value: charge.amount,
          variant: 'body2' as const
        });
      });
    }

    // Discount
    if (singleSalesReturn.discount?.amount && singleSalesReturn.discount.amount > 0) {
      const discountLabel = singleSalesReturn.discount.isPercentage
        ? `Discount (${singleSalesReturn.discount.amount}%)`
        : 'Discount';
      summaryItems.push({
        label: discountLabel,
        value: singleSalesReturn.discount.amount,
        variant: 'body2' as const
      });
    }

    // Round Off
    if (singleSalesReturn.roundOff) {
      const roundOffValue = Math.round(totals.amount + totals.tax) - (totals.amount + totals.tax);
      if (Math.abs(roundOffValue) > 0) {
        summaryItems.push({
          label: 'Round Off',
          value: roundOffValue,
          variant: 'body2' as const
        });
      }
    }

    // Grand Total
    const grandTotal = singleSalesReturn.totalAmount;
    summaryItems.push({
      label: 'Grand Total',
      value: grandTotal,
      bold: true,
      variant: 'body1' as const
    });

    return summaryItems;
  };

  // Prepare custom fields for payment info
  const prepareCustomFields = () => {
    if (!singleSalesReturn) return [];

    const fields = [];

    if (singleSalesReturn.receivedAmount >= 0) {
      fields.push({
        label: "Paid Amount",
        value: singleSalesReturn.receivedAmount
      });
    }

    if (singleSalesReturn.balanceAmount >= 0) {
      fields.push({
        label: "Balance Due",
        value: singleSalesReturn.balanceAmount
      });
    }

    return fields;
  };

  if (!singleSalesReturn && !loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <DocumentHeader
          heading='Sales Return'
          subtitle='Sales Return not found'
          showBackButton={true}
        />
      </Box>
    );
  }

  const totals = calculateTotals();
  const tableData = prepareTableData();
  const tableColumns = prepareTableColumns();
  const summaryItems = prepareSummaryItems();
  const customFields = prepareCustomFields();

  return (
    <>
      <DocumentHeader
        heading='Sales Return'
        subtitle={`#${singleSalesReturn?.salesReturnNumber || 'Loading...'}`}
        status={singleSalesReturn?.status || 'UNPAID'}
        statusColor={singleSalesReturn?.status === 'PAID' ? 'success' :
          singleSalesReturn?.status === 'PARTIAL_PAID' ? 'info' : singleSalesReturn?.status === 'UNPAID' ? 'warning' : 'primary'}
        showBackButton={true}
      />

      <Box display='flex' justifyContent='flex-end' mb={3} gap={2}>

        <PrintButtons
          onPrint={onPrint}
          isPrinting={isPrinting}
          printLabel='Print SR'
          pdfLabel='Export PDF'
          disabled={loading || !singleSalesReturn}
          showPDF={false} // Remove PDF button since we don't have export functionality yet
        />
        {/* <CommonCreateButton
          label="Record Payment"
          onClick={() => alert('Record Payment clicked')}
          disabled={singleSalesReturn?.status === 'PAID'}
        /> */}
      </Box>

      <PrintableContainer id="sales_return_print">
        <CommonInvoiceTemplate
          documentType={singleSalesReturn?.salesReturnType || "SALES_RETURN"}
          title={singleSalesReturn?.gstType === 'GST' ? "SALES RETURN" : "SALES RETURN"}
          companyName={businessInfo?.businessName}
          companyAddress={
            (businessInfo?.address) ? (`${businessInfo.address?.[0]?.line1} ${businessInfo.address?.[0]?.city} ${businessInfo.address?.[0]?.state}  ${businessInfo.address?.[0]?.pinCode}`) : 'Address not mentioned'
          }
          companyPhone={(businessInfo?.contact?.phone?.[0]?.phoneNo) ? (`${businessInfo?.contact?.phone?.[0]?.phoneNo}`) : '+91 0000000000'}
          companyGSTIN={singleSalesReturn?.gstType === 'GST' ? taxInfo?.taxDetails?.gstNumber : ""}
          documentNumber={singleSalesReturn?.salesReturnNumber || ''}
          documentDate={formatDate(singleSalesReturn?.salesReturnDate || singleSalesReturn?.date || '')}
          dueDate={formatDate(singleSalesReturn?.dueDate || '')}
          partyHeading='Party Info.'
          partyName={singleSalesReturn?.party?.partyName || 'N/A'}
          partyLocation={singleSalesReturn?.party?.location}
          partyAddress={
            (singleSalesReturn?.party?.address) ? (`${singleSalesReturn.party.address.line1} ${singleSalesReturn.party.address.city} ${singleSalesReturn.party.address.state}  ${singleSalesReturn.party.address.pinCode}`) : 'Address not available'
          }
          partyPhone={singleSalesReturn?.party?.contact?.phone?.[0]?.phoneNo || 'N/A'}
          partyGSTIN={singleSalesReturn?.party?.gstNumber || 'N/A'}
          tableColumns={tableColumns}
          tableData={tableData}
          tableTotals={{
            quantity: totals.quantity,
            taxableAmount: totals.taxableAmount,
            tax: totals.tax,
            amount: totals.amount
          }}
          amounts={{
            totalAmount: singleSalesReturn?.totalAmount,
            taxableAmount: singleSalesReturn?.taxableAmount,
            subtotal: singleSalesReturn?.subtotal,
            receivedAmount: singleSalesReturn?.receivedAmount,
            balanceAmount: singleSalesReturn?.balanceAmount
          }}
          summaryItems={summaryItems}
          customFields={customFields}
          showLoading={loading}
          showLoadingText="Fetching Sales Return..."
          // s={singleInvoice?.s || ''}
          termsConditions={singleSalesReturn?.terms || ''}
          gstType={singleSalesReturn?.gstType}
          invoiceStatus={singleSalesReturn?.status}
        />
      </PrintableContainer>
    </>
  )
}

export default ShowSalesReturn